import React from "react";
import Card from 'react-bootstrap/Card';
import Accordion from 'react-bootstrap/Accordion';
import MaterialCostsTable from "../../../../../../components/cost-breakdown/material-costs-table";
import LaborCostsTable from "../../../../../../components/cost-breakdown/labor-costs-table";
import SubcontractorCostsTable from "../../../../../../components/cost-breakdown/subcontractor-costs-table";
import TotalCostsRow from "../../../../../../components/cost-breakdown/total-costs-row";
import SingleCostTypeTable from "../../../../../../components/cost-breakdown/single-cost-type-table";

/**
 * CostBreakdownContent - Main content component for cost breakdown display
 * 
 * SUMMARY:
 * Displays cost breakdown data in either accordion format (for "all" cost type) or
 * single card format (for specific cost types). Handles loading states and empty states.
 * 
 * RESPONSIBILITIES:
 * - Display loading state
 * - Render accordion for "all" cost type with multiple sections
 * - Render single card for specific cost types
 * - Display empty state when no data available
 * - Pass data and handlers to table components
 * 
 * PROPS:
 * @param {boolean} isLoading - Loading state
 * @param {string} costType - Cost type (material, labor, subcontractor, all)
 * @param {Array} costDetails - Array of cost detail objects
 * @param {Array} resourceAllocations - Array of resource allocation objects
 * @param {string} costTypeLabel - Label for cost type
 * @param {string} stagePath - Formatted stage path string
 * @param {Function} getAllocationCost - Function to calculate allocation cost
 * @param {Function} formatHours - Function to format hours
 * @param {Function} handleViewPO - Handler to open PO modal
 * @param {Function} handleViewWO - Handler to open WO modal
 * @param {Function} handleViewInvoice - Handler to open invoice modal
 * @param {number} totalCost - Total cost value
 * 
 * COMPONENTS RENDERED:
 * - Accordion: For "all" cost type with multiple sections
 * - Card: For single cost type display
 * - MaterialCostsTable: Material costs table
 * - LaborCostsTable: Labor costs table
 * - SubcontractorCostsTable: Subcontractor costs table
 * - SingleCostTypeTable: Single cost type table
 * - TotalCostsRow: Total costs row
 */
const CostBreakdownContent = ({
  isLoading,
  costType,
  costDetails,
  resourceAllocations,
  costTypeLabel,
  stagePath,
  getAllocationCost,
  formatHours,
  handleViewPO,
  handleViewWO,
  handleViewInvoice,
  totalCost
}) => {
  if (isLoading) {
    return (
      <div className="text-center p-4">Loading...</div>
    );
  }

  const hasData = (costType === "labor" && resourceAllocations.length > 0) ||
    (costType !== "labor" && costDetails.length > 0) ||
    (costType === "all" && (costDetails.length > 0 || resourceAllocations.length > 0));

  if (!hasData) {
    return (
      <Card className="border-0 mb-3">
        <Card.Header className="sections d-flex justify-content-between align-items-center bg-body">
          <h6 className="mb-0">{stagePath} - {costTypeLabel} Cost Breakdown</h6>
        </Card.Header>
        <Card.Body>
          <div className="text-center p-4">No data available</div>
        </Card.Body>
      </Card>
    );
  }

  if (costType === "all") {
    return (
      <>
        <Accordion defaultActiveKey={['0', '1', '2']} className='breakdown-container' alwaysOpen>
          {/* Material section */}
          {costDetails.some(item => item.type === 'PO' || item.type === 'WPO') && (
            <Accordion.Item eventKey="0">
              <Accordion.Header>
                <h6 className="mb-0">{stagePath} - Material Costs</h6>
              </Accordion.Header>
              <Accordion.Body className="p-0">
                <MaterialCostsTable
                  costDetails={costDetails}
                  handleViewPO={handleViewPO}
                  handleViewInvoice={handleViewInvoice}
                />
              </Accordion.Body>
            </Accordion.Item>
          )}

          {/* Subcontractor section */}
          {costDetails.some(item => item.type === 'Subcontractor Invoice') && (
            <Accordion.Item eventKey="2">
              <Accordion.Header>
                <h6 className="mb-0">{stagePath} - Contractor Costs</h6>
              </Accordion.Header>
              <Accordion.Body className="p-0">
                <SubcontractorCostsTable
                  costDetails={costDetails}
                  handleViewPO={handleViewPO}
                  handleViewWO={handleViewWO}
                  handleViewInvoice={handleViewInvoice}
                />
              </Accordion.Body>
            </Accordion.Item>
          )}

          {/* Labor section */}
          {resourceAllocations.length > 0 && (
            <Accordion.Item eventKey="1">
              <Accordion.Header>
                <h6 className="mb-0">{stagePath} - Resource Costs</h6>
              </Accordion.Header>
              <Accordion.Body className="p-0">
                <LaborCostsTable
                  resourceAllocations={resourceAllocations}
                  getAllocationCost={getAllocationCost}
                  formatHours={formatHours}
                />
              </Accordion.Body>
            </Accordion.Item>
          )}
        </Accordion>

        {/* Total card */}
        <Card className="border-0 mb-3 mt-3">
          <Card.Body className="p-0">
            <TotalCostsRow totalCost={totalCost} />
          </Card.Body>
        </Card>
      </>
    );
  }

  // Single cost type display
  return (
    <Card className="border-0 mb-3">
      <Card.Header className="sections d-flex justify-content-between align-items-center bg-body">
        <h6 className="mb-0">{stagePath} - {costTypeLabel} Cost Breakdown</h6>
      </Card.Header>
      <Card.Body className="p-0">
        <SingleCostTypeTable
          costType={costType}
          costDetails={costDetails}
          resourceAllocations={resourceAllocations}
          handleViewPO={handleViewPO}
          handleViewWO={handleViewWO}
          handleViewInvoice={handleViewInvoice}
          getAllocationCost={getAllocationCost}
          formatHours={formatHours}
        />
      </Card.Body>
    </Card>
  );
};

export default CostBreakdownContent;
